import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { createGmailDraft } from '../gmail/drafts';
import {
  addLeadsToList,
  createCopySet,
  createLead,
  createLeadList,
  createSalesTask,
  deleteLead,
  deleteLeadList,
  deleteSalesTask,
  fetchLinkedInDays,
  fetchSalesTasks,
  removeMembership,
  renameLeadList,
  saveLinkedInDay,
  updateSalesTask,
  fetchCompanies,
  fetchCopySets,
  fetchLeadLists,
  fetchLeadStages,
  fetchLeads,
  fetchMemberships,
  fetchAllMemberships,
  fetchSalesAccess,
  createCompany,
  createLeadsBulk,
  deleteCompany,
  mergeCompanies,
  mergeLeads,
  updateCompany,
  updateCopySet,
  saveCopyEdit,
  updateLead,
  updateMembership,
} from './queries';
import type { JourneyCommand } from './journeyOperations';
import { legacyMergeLeads } from './legacyMergeLeads';
import type { Lead } from './types';
import { useProspectingAvailability } from './useProspectingAvailability';
import { assertNoPendingLegacyJourney } from './journeyPending';
import type {
  BulkLeadDefaults,
  BulkLeadRow,
  CompanyUpdate,
  CopySetInput,
  LeadInput,
  LeadUpdate,
  LinkedInDayInput,
  MembershipUpdate,
  SalesTaskInput,
  SalesTaskUpdate,
} from './queries';
import type { CopyEditCommand } from './copyEditConfirmation';

export const trackerKeys = {
  all: ['tracker'] as const,
  access: () => [...trackerKeys.all, 'access'] as const,
  stages: () => [...trackerKeys.all, 'stages'] as const,
  leads: () => [...trackerKeys.all, 'leads'] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

/** Whether the signed-in person is behind the sales gate (mirrors RLS). */
export function useSalesAccess() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: trackerKeys.access(),
    queryFn: () => fetchSalesAccess(client),
    enabled: access.status === 'approved',
    staleTime: Infinity,
    retry: false,
  });
}

export function useLeadStages() {
  const { client } = useAuth();
  return useQuery({
    queryKey: trackerKeys.stages(),
    queryFn: () => fetchLeadStages(client),
    staleTime: Infinity,
    retry: false,
  });
}

export function useLeads() {
  const { client } = useAuth();
  return useQuery({
    queryKey: trackerKeys.leads(),
    queryFn: () => fetchLeads(client),
    retry: false,
  });
}

export function useCreateLead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: LeadInput) => createLead(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

export function useUpdateLead() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const updatedBy = useActorId();
  return useMutation({
    mutationFn: ({ leadId, changes }: { leadId: string; changes: LeadUpdate }) =>
      updateLead(client, leadId, { ...changes, updated_by: updatedBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

export function useDeleteLead() {
  const { client } = useAuth();
  const actor = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: JourneyCommand | string) => {
      if (typeof command === 'string') assertNoPendingLegacyJourney(actor);
      return deleteLead(client, command);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

export function useCompanies() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'companies'] as const,
    queryFn: () => fetchCompanies(client),
    retry: false,
  });
}

export function useLeadLists() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'lists'] as const,
    queryFn: () => fetchLeadLists(client),
    retry: false,
  });
}

export function useMemberships(listId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'memberships', listId] as const,
    queryFn: () => fetchMemberships(client, listId ?? ''),
    enabled: listId !== null,
    retry: false,
  });
}

export function useCopySets() {
  const { client } = useAuth();
  const { message_enabled } = useProspectingAvailability();
  return useQuery({
    queryKey: [...trackerKeys.all, 'copySets', message_enabled] as const,
    queryFn: () => fetchCopySets(client, message_enabled),
    retry: false,
  });
}

const tasksKey = () => [...trackerKeys.all, 'salesTasks'] as const;
const linkedInKey = () => [...trackerKeys.all, 'linkedin'] as const;

export function useSalesTasks() {
  const { client } = useAuth();
  return useQuery({
    queryKey: tasksKey(),
    queryFn: () => fetchSalesTasks(client),
    retry: false,
  });
}

export function useCreateSalesTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: SalesTaskInput) => createSalesTask(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKey() });
    },
  });
}

export function useUpdateSalesTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, changes }: { taskId: string; changes: SalesTaskUpdate }) =>
      updateSalesTask(client, taskId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKey() });
    },
  });
}

export function useDeleteSalesTask() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => deleteSalesTask(client, taskId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tasksKey() });
    },
  });
}

export function useLinkedInDays() {
  const { client } = useAuth();
  return useQuery({
    queryKey: linkedInKey(),
    queryFn: () => fetchLinkedInDays(client),
    retry: false,
  });
}

export function useSaveLinkedInDay() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: LinkedInDayInput) => saveLinkedInDay(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: linkedInKey() });
    },
  });
}

const listsKey = () => [...trackerKeys.all, 'lists'] as const;
const membershipsPrefix = () => [...trackerKeys.all, 'memberships'] as const;

export function useCreateLeadList() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: ({ name, type }: { name: string; type: string }) =>
      createLeadList(client, name, type, createdBy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listsKey() });
    },
  });
}

export function useRenameLeadList() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, name }: { listId: string; name: string }) =>
      renameLeadList(client, listId, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listsKey() });
    },
  });
}

export function useDeleteLeadList() {
  const { client } = useAuth();
  const actor = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: JourneyCommand | string) => {
      if (typeof command === 'string') assertNoPendingLegacyJourney(actor);
      return deleteLeadList(client, command);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listsKey() });
      void queryClient.invalidateQueries({ queryKey: membershipsPrefix() });
    },
  });
}

export function useAddLeadsToList() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, leadIds }: { listId: string; leadIds: string[] }) =>
      addLeadsToList(client, listId, leadIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membershipsPrefix() });
    },
  });
}

export function useRemoveMembership() {
  const { client } = useAuth();
  const actor = useActorId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: JourneyCommand | string) => {
      if (typeof command === 'string') assertNoPendingLegacyJourney(actor);
      return removeMembership(client, command);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membershipsPrefix() });
    },
  });
}

export function useUpdateCompany() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, changes }: { companyId: string; changes: CompanyUpdate }) =>
      updateCompany(client, companyId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...trackerKeys.all, 'companies'] as const });
    },
  });
}

const companiesKey = () => [...trackerKeys.all, 'companies'] as const;

export function useCreateCompany() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: { name: string; website: string; industry: string }) =>
      createCompany(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companiesKey() });
    },
  });
}

export function useDeleteCompany() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => deleteCompany(client, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companiesKey() });
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

export function useMergeCompanies() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      keeper,
      loser,
    }: {
      keeper: { id: string; name: string };
      loser: { id: string; name: string };
    }) => mergeCompanies(client, keeper, loser),
    onSuccess: () => {
      // Leads and tasks were retargeted, so everything company-adjacent moves.
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
    },
  });
}

export function useCreateCopySet() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: CopySetInput & { name: string }) =>
      createCopySet(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...trackerKeys.all, 'copySets'] as const });
    },
  });
}

export function useUpdateCopySet() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ copySetId, changes }: { copySetId: string; changes: CopySetInput }) =>
      updateCopySet(client, copySetId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...trackerKeys.all, 'copySets'] as const });
    },
  });
}

export function useSaveCopyEdit() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: CopyEditCommand) => saveCopyEdit(client, command),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...trackerKeys.all, 'copySets'] as const });
    },
  });
}

export function useUpdateMembership(listId: string | null) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  // The list argument no longer scopes invalidation (prefix below covers every
  // memberships query), but callers still pass it; keep the signature stable.
  void listId;
  return useMutation({
    mutationFn: ({ membershipId, changes }: { membershipId: string; changes: MembershipUpdate }) =>
      updateMembership(client, membershipId, changes),
    onSuccess: () => {
      // Prefix, not the single list: the same rows feed the list grids, the
      // dashboard (all memberships), and the lead panel's cadence slots.
      void queryClient.invalidateQueries({ queryKey: membershipsPrefix() });
    },
  });
}

/** Creates a personalized Gmail draft in the caller's own Gmail. */
export function useComposeDraft() {
  const { client } = useAuth();
  return useMutation({
    mutationFn: (input: { to: string; subject: string; body: string }) =>
      createGmailDraft(client, { ...input, signatureHtml: '' }),
  });
}

export function useAllMemberships() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'memberships', 'all'] as const,
    queryFn: () => fetchAllMemberships(client),
    retry: false,
  });
}

export function useImportLeads() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: ({
      rows,
      stageId,
      defaults,
    }: {
      rows: BulkLeadRow[];
      stageId: string;
      // What the whole paste shares — Direction and Source (batch 1b).
      // Optional so the existing call sites keep working unchanged.
      defaults?: BulkLeadDefaults;
    }) => createLeadsBulk(client, rows, stageId, createdBy, defaults),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

export function useMergeLeads() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const actor = useActorId();
  return useMutation({
    mutationFn: (command: JourneyCommand | { keeper: Lead; loser: Lead }) => {
      if ('kind' in command) return mergeLeads(client, command);
      assertNoPendingLegacyJourney(actor);
      return legacyMergeLeads(client, command.keeper, command.loser, actor);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.all });
    },
  });
}

/**
 * Deals raised in the recent past, for the dashboard's "what moved".
 *
 * A conversion has no dated event anywhere else the dashboard loads: the
 * promote RPC records it as a `note` whose only marker is the words
 * "Promoted to deal:" in its body, and the activity query deliberately does
 * not fetch bodies. `deals.created_at` is the exact answer.
 *
 * THE CAVEAT, and it is why the caller must check a permission: `deals`
 * carries a RESTRICTIVE policy, `deals_module_gate`, requiring the Deals
 * module. A sales member without it reads NOTHING here — not an error, an
 * empty list. Rendered blind, that says "nothing converted this week" to
 * somebody who simply cannot see deals, which is the exact shape of quiet
 * wrongness this module has spent a month removing. The dashboard says so
 * instead.
 */
export function useRecentDeals(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...trackerKeys.all, 'recent-deals'] as const,
    queryFn: async (): Promise<{ lead_id: string | null; created_at: string }[]> => {
      const { data, error } = await client.from('deals').select('lead_id, created_at');
      if (error) {
        throw new Error(error.message);
      }
      return Array.isArray(data) ? data : [];
    },
    enabled,
    retry: false,
  });
}
